"use client";

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";



import type { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client";
import { ReducedProductData } from "@/lib/storefront/product/product";
import { determineVariableAttributeValuesAsString } from "@/lib/storefront/product/productHelper";
import { buildGa4Item, resetEcommerce, sendGTMEventIfAnalyticsAllowed, toCustomerType } from "@/lib/ga4TrackingHelper";
import { CardPrices, determineCardPrices } from "@/lib/price-utils";
import type { MarketChannel } from "@/lib/market/market-channel";
import type { CommonPageContext } from "@/lib/tracking/tracking";
import {
   DEFAULT_SEARCH_RESULT_PAGE_SIZES,
   DEFAULT_SEARCH_RESULT_SORT_ORDER,
   SEARCH_PARAMS_FF_SEARCH_PARAMS,
   SEARCH_PARAMS_MAX_PRICE,
   SEARCH_PARAMS_MIN_PRICE,
   SEARCH_PARAMS_PAGE,
   SEARCH_PARAMS_PAGE_SIZE,
   SEARCH_PARAMS_SORT_ORDER,
} from "@/constants/ui";





export interface ProductImpressionsTrackingProps {
  products: ReducedProductData[]
  list: string
  isoLocale: string
  sbLocale?: string
  channel?: MarketChannel
   categoryPath?: string[]
   pageType?: string
}

interface ProductPricesBatchResponse {
   pricesByProductId?: Record<string, ProductPriceContainerCtnrROV1 | undefined>
   isB2B?: boolean
   isAnonymous?: boolean
}

const ProductImpressionsTrackingClient = (props: ProductImpressionsTrackingProps) => {
   const [tracked, setTracked] = useState(false)
   const [consentRetryKey, setConsentRetryKey] = useState(0)
   const [commonPageContext, setCommonPageContext] = useState<CommonPageContext | undefined>()
   const [pricesByProductId, setPricesByProductId] = useState<
      Record<string, ProductPriceContainerCtnrROV1 | undefined> | undefined
   >()
   const [pricesLoaded, setPricesLoaded] = useState(false)
   const lastPushedPaginationRef = useRef<string | null>(null)
   const searchParams = useSearchParams()

   useEffect(() => {
      if (commonPageContext) {
         return
      }

      const controller = new AbortController()
      const contextUrl = props.sbLocale
         ? `/api/storefront/context?sbLocale=${encodeURIComponent(props.sbLocale)}`
         : "/api/storefront/context"

      fetch(contextUrl, {
         cache: "no-store",
         signal: controller.signal,
      })
         .then((response) => (response.ok ? response.json() : undefined))
         .then((context: CommonPageContext | undefined) => {
            if (context) {
               setCommonPageContext(context)
            }
         })
         .catch((error) => {
            if (error?.name !== "AbortError") {
               console.error("Could not load storefront context for product impressions tracking", error)
            }
         })

      return () => controller.abort()
   }, [commonPageContext, props.sbLocale])

   useEffect(() => {
      if (!commonPageContext) {
         return
      }

      const productIds = Array.from(new Set(props.products.map((product) => product.id).filter(Boolean)))
      if (productIds.length === 0) {
         setPricesByProductId({})
         setPricesLoaded(true)
         return
      }

      setPricesByProductId(undefined)
      setPricesLoaded(false)

      const controller = new AbortController()
      let cancelled = false
      const searchParams = new URLSearchParams()
      if (props.sbLocale) {
         searchParams.set("sbLocale", props.sbLocale)
      }
      if (props.channel) {
         searchParams.set("channel", props.channel)
      }

      fetch(`/api/storefront/product-prices?${searchParams.toString()}`, {
         method: "POST",
         cache: "no-store",
         headers: {
            "Content-Type": "application/json",
         },
         body: JSON.stringify({ productIds }),
         signal: controller.signal,
      })
         .then((response) => (response.ok ? response.json() : undefined))
         .then((data: ProductPricesBatchResponse | undefined) => {
            if (cancelled) {
               return
            }
            setPricesByProductId(data?.pricesByProductId ?? {})
            setPricesLoaded(true)
         })
         .catch((error) => {
            if (cancelled) {
               return
            }
            if (error?.name !== "AbortError") {
               console.error("Could not load prices for product impressions tracking", error)
            }
            setPricesByProductId({})
            setPricesLoaded(true)
         })

      return () => {
         cancelled = true
         controller.abort()
      }
   }, [commonPageContext, props.products, props.sbLocale])

   useEffect(() => {
      const handleConsentUpdate = () => setConsentRetryKey((current) => current + 1)

      window.addEventListener("OneTrustGroupsUpdated", handleConsentUpdate)
      return () => window.removeEventListener("OneTrustGroupsUpdated", handleConsentUpdate)
   }, [])

   useEffect(() => {
      // Determine pagination context
      const pageParam = Number(searchParams?.get(SEARCH_PARAMS_PAGE) ?? 1)
      const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1
      const pageSizeParam = Number(searchParams?.get(SEARCH_PARAMS_PAGE_SIZE) ?? DEFAULT_SEARCH_RESULT_PAGE_SIZES[0])
      const pageSize =
         Number.isFinite(pageSizeParam) && pageSizeParam > 0 ? pageSizeParam : DEFAULT_SEARCH_RESULT_PAGE_SIZES[0]
      const offset = (page - 1) * pageSize
      const paginationKey = `${page}:${pageSize}`
      const sortOrder = searchParams?.get(SEARCH_PARAMS_SORT_ORDER)
      const isSortActive = !!sortOrder && sortOrder !== DEFAULT_SEARCH_RESULT_SORT_ORDER[0]
      const isFilterActive =
         !!searchParams?.get(SEARCH_PARAMS_FF_SEARCH_PARAMS) ||
         !!searchParams?.get(SEARCH_PARAMS_MIN_PRICE) ||
         !!searchParams?.get(SEARCH_PARAMS_MAX_PRICE)
      if (isSortActive || isFilterActive) {
         return
      }
      if (!commonPageContext) {
         return
      }
      if (!pricesLoaded) {
         return
      }

      // Only fire on initial mount or when pagination changes.
      if (!tracked || lastPushedPaginationRef.current !== paginationKey) {
         const customer_type = toCustomerType(commonPageContext.isAnonymous, commonPageContext.isB2B)
         const topCats = props.categoryPath?.filter(Boolean) ?? []
         const firstCategoryPath = topCats.join("/")
         const depth = topCats.length
         const pagetyp =
            props.pageType ??
            (depth >= 4 ? "category_l4" : depth === 3 ? "category_l3" : depth === 2 ? "category_l2" : "category_l1")

         const hasCategoryPath = topCats.length > 0
         const listIdBase = hasCategoryPath
            ? (topCats.at(-1) || props.list || "list").toLowerCase()
               .replace(/[^a-z0-9]+/g, "_")
               .replace(/^_+|_+$/g, "")
            : undefined
         const item_list_id = listIdBase ? `cat_${pagetyp.split('_')[1]}_${listIdBase}` : undefined
         const item_list_name = hasCategoryPath ? topCats.at(-1) || props.list || undefined : undefined

         const items = props.products.map((p, idx) => {
            let priceGross: number | undefined
            try {
               const prices = pricesByProductId?.[p.id] ?? p.effectivePrices
               const cp: CardPrices | undefined = prices
                  ? determineCardPrices(props.isoLocale, false, prices, p.ecoTax, 1, p.id)
                  : undefined
               priceGross = cp?.salePrice
            } catch (e) {
               // fallback: omit price if price resolution fails
            }
            return buildGa4Item({
               product: p,
               priceGross,
               currency: p.currency || "EUR",
               quantity: 1,
               variant: determineVariableAttributeValuesAsString(p),
               categoryPath: firstCategoryPath,
               index: offset + idx,
               item_list_id,
               item_list_name,
            })
         })

         const ecommerce: any = {
            items,
         }
         if (item_list_id) ecommerce.item_list_id = item_list_id
         if (item_list_name) ecommerce.item_list_name = item_list_name
         if (topCats[0]) ecommerce.category_l1 = topCats[0]
         if (topCats[1]) ecommerce.category_l2 = topCats[1]
         if (topCats[2]) ecommerce.category_l3 = topCats[2]
         if (topCats[3]) ecommerce.category_l4 = topCats[3]

         sendGTMEventIfAnalyticsAllowed(resetEcommerce())
         const sentPayload = sendGTMEventIfAnalyticsAllowed({
            event: "view_item_list",
            pagetyp,
            customer_type,
            ecommerce,
         })
         sendGTMEventIfAnalyticsAllowed(resetEcommerce())
         if (sentPayload) {
            setTracked(true)
            lastPushedPaginationRef.current = paginationKey
         } else {
            window.setTimeout(() => setConsentRetryKey((current) => current + 1), 500)
         }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, consentRetryKey, commonPageContext, pricesLoaded, pricesByProductId])

  return <span className={"hidden"}>ProductImpressionsTrackingClient</span>
}
export default ProductImpressionsTrackingClient

/*
{
   "event": "product_impressions",
   "ecommerce": {
      "currencyCode": "EUR",
      "impressions": [
         {
            "brand": "UDOBÄR",
            "category": "Umwelt",
            "id": "A273075",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Mülltonne Citybac Classic aus recyceltem Material 120 l",
            "position": 34,
            "variant": "fernblau // RAL5023/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Abfallentsorgung",
            "id": "A273074",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Mülltonne Citybac Classic aus recyceltem Material 120 l",
            "position": 35,
            "variant": "zinkgelb // RAL1018/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Schnell  preiswert",
            "id": "A273077",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Mülltonne Citybac Classic aus recyceltem Material 120 l",
            "position": 36,
            "variant": "resedagrün // RAL6011/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Betrieb",
            "id": "A273526",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Mülltonne Citybac Classic aus recyceltem Material 120 l",
            "position": 37,
            "variant": "blassbraun // RAL8025/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Lager Mülltonnen",
            "id": "A273533",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Mülltonne Citybac aus recyceltem Material 120 l",
            "position": 38
         },
         {
            "brand": "UDOBÄR",
            "category": "Schnell  preiswert",
            "id": "A273538",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Scharnierdeckel Citybac für Mülltonne gelb",
            "position": 39,
            "variant": "gelb/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Betrieb",
            "id": "A273537",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Scharnierdeckel Citybac für Mülltonne blau",
            "position": 40,
            "variant": "blau/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Lager Mülltonnen",
            "id": "A273534",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Scharnierdeckel Citybac für Mülltonne schwarzgrau",
            "position": 41,
            "variant": "schwarzgrau/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Aktionskategorien",
            "id": "A273536",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Scharnierdeckel Citybac für Mülltonne braun",
            "position": 42,
            "variant": "braun/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Schnell  preiswert",
            "id": "A273538",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Scharnierdeckel Citybac für Mülltonne gelb",
            "position": 43,
            "variant": "gelb/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Betrieb",
            "id": "A273537",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Scharnierdeckel Citybac für Mülltonne blau",
            "position": 44,
            "variant": "blau/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Reinigungstücher",
            "id": "A273963",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Reinigungstücher Multiuse Bio 100 Tücher Stoff",
            "position": 45,
            "variant": "Pflanzenfasergewebe/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Reinigungstücher",
            "id": "A273961",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Reinigungstücher Multiuse 100 Tücher Stoff",
            "position": 46,
            "variant": "Gewebe/"
         },
         {
            "brand": "UDOBÄR",
            "category": "Reinigungstücher",
            "id": "A273962",
            "list": "Category Schnelllieferprogramm",
            "name": "UDOBÄR Reinigungstücher Power Scrub 80 Tücher Stoff",
            "position": 47
         },
         {
            "brand": "PAVOY",
            "category": "Wäscheschränke",
            "id": "E13709",
            "list": "Category Zuletzt angesehen",
            "name": "PAVOY Wäscheschrank Basis RAL7035",
            "position": 1
         },
         {
            "brand": "B-Safety",
            "category": "Seifen Cremes und Desinfektionsmittel",
            "id": "A147347",
            "list": "Category Zuletzt angesehen",
            "name": "B-Safety Cremeseife PLUM Fresh",
            "position": 2,
            "variant": ""
         },
         {
            "category": "Persönliche Schutzausrüstung",
            "id": "E19617",
            "list": "Category Zuletzt angesehen",
            "name": "Nitril-Schutzhandschuhe Ultranitril",
            "position": 3
         },
         {
            "brand": "CWS",
            "category": "Seifen Cremes und Desinfektionsmittel",
            "id": "A523504",
            "list": "Category Zuletzt angesehen",
            "name": "CWS Seifenschaumkonzentrat Standard",
            "position": 4
         },
         {
            "category": "Persönliche Schutzausrüstung",
            "id": "E20428",
            "list": "Category Zuletzt angesehen",
            "name": "Damen-Arbeitsmantel mit Druckknöpfen und Rückenschlitz",
            "position": 5
         },
         {
            "category": "Persönliche Schutzausrüstung",
            "id": "A221396",
            "list": "Category Zuletzt angesehen",
            "name": "Einweg-Schutzoverall weiß Größe XL",
            "position": 6,
            "variant": "XL/"
         }
      ]
   },
   "gtm.uniqueEventId": 155
}*/
